/**
 * Structure de l'état de simulation, telle que vue par les clients.
 *
 * Séparation (voir docs/ARCHITECTURE.md) :
 * - Monde partagé (`WorldPublic`) : horloge, météo, demande touristique de la
 *   vallée, marché des fournisseurs, joueurs et postes. Visible de tous.
 * - Station (`StationPublic` + remontées, pistes, bâtiments, skieurs) :
 *   possédée par un joueur, visible de tous les joueurs du monde.
 * - Joueur (`PlayerPrivate`) : trésorerie, tarifs, stocks, commandes,
 *   finances, alertes. Envoyé uniquement à son propriétaire.
 *
 * Chaque entité porte un `rev` incrémenté à chaque modification : le serveur
 * n'envoie que les entités dont le `rev` a changé depuis le tick précédent.
 */
import type { BuildingKind, LiftKind, SupplyKind } from './catalog';
import type { Vec2 } from './geometry';
import type { SimSpeed } from './time';

export type EntityId = number;

/* ------------------------------------------------------------------ */
/* Horloge                                                              */
/* ------------------------------------------------------------------ */

export interface ClockWire {
  /** Numéro de tick serveur (monotone). */
  tick: number;
  /** Temps de simulation (s). */
  sim: number;
  /** Horloge de jeu (s depuis le début de saison). */
  game: number;
  /** Secondes de jeu par seconde de simulation. */
  scale: number;
  /** Vitesse choisie (0 = pause). */
  speed: SimSpeed;
  /** Faux si la simulation est suspendue (personne n'est connecté). */
  running: boolean;
}

/* ------------------------------------------------------------------ */
/* Monde partagé                                                        */
/* ------------------------------------------------------------------ */

export type WeatherRegime = 'grand_beau' | 'nuageux' | 'neige' | 'tempete' | 'redoux';

export interface ForecastDay {
  day: number;
  regime: WeatherRegime;
  tMin: number;
  tMax: number;
  snowCm: number;
  windMax: number;
}

export interface Weather {
  regime: WeatherRegime;
  /** Température au pied des pistes (°C). */
  temp: number;
  /** Vent (km/h). */
  wind: number;
  /** Chutes de neige en cours (cm par heure de jeu). */
  snowRate: number;
  /** Couverture nuageuse 0..1. */
  cloud: number;
  /** Neige tombée sur les 24 dernières heures de jeu (cm). */
  snow24h: number;
  /** Prévision des 24 prochaines heures (cm). */
  snowNext24h: number;
  forecast: ForecastDay[];
}

export interface RegionState {
  /** Indice de demande touristique de la vallée (1 = jour de semaine normal). */
  demandIndex: number;
  weekend: boolean;
  holiday: string | null;
  dateLabel: string;
}

export interface MarketOffer {
  supply: SupplyKind;
  /** Poste fournisseur (géré par une IA tant qu'aucun joueur ne le prend). */
  supplier: string;
  price: number;
  deliveryHours: number;
}

export type PosteRole = 'station' | 'supermarche' | 'transport';

export interface PosteState {
  role: PosteRole;
  name: string;
  controller: 'player' | 'ai';
  playerId: string | null;
}

export interface PlayerPublic {
  id: string;
  name: string;
  role: PosteRole | 'observer';
  online: boolean;
}

export interface WorldPublic {
  id: string;
  name: string;
  mapId: string;
  hostId: string | null;
  weather: Weather;
  region: RegionState;
  market: MarketOffer[];
  postes: PosteState[];
  players: PlayerPublic[];
}

/* ------------------------------------------------------------------ */
/* Station (publique)                                                   */
/* ------------------------------------------------------------------ */

export type LiftStatus = 'construction' | 'open' | 'closed' | 'broken' | 'wind' | 'off-hours';

/** Position du câble : s(t) = s + v * (t - t0). */
export interface CableState {
  s: number;
  t0: number;
  v: number;
}

export interface Lift {
  id: EntityId;
  rev: number;
  kind: LiftKind;
  name: string;
  bottom: Vec2;
  top: Vec2;
  status: LiftStatus;
  /** Avancement du chantier 0..1. */
  progress: number;
  /** Usure 0..1 (augmente avec le fonctionnement, remise à zéro à la réparation). */
  wear: number;
  cable: CableState;
  /** Personnes dans la file. */
  queue: number;
  /** Attente estimée (s de simulation). */
  waitSec: number;
  /** Débit mesuré (personnes par heure de simulation). */
  throughput: number;
  /** Embarquements depuis l'ouverture du jour. */
  rides: number;
  /** Teinte des sièges (index de palette). */
  color: number;
  /** Fermeture manuelle par le joueur. */
  manualClosed: boolean;
  /** Fin de réparation (horloge de jeu) si en panne et réparation lancée. */
  repairUntil: number | null;
}

export type Difficulty = 'verte' | 'bleue' | 'rouge' | 'noire';

/**
 * Nœud du réseau : "V" (village), "B<id>" (bas de remontée), "T<id>" (haut),
 * "H<id>" (bâtiment), "P<id>@<s>" (point d'une piste à s mètres de son départ,
 * le long de sa trajectoire : raccord, ou extrémité qui ne rejoint rien).
 */
export type NodeKey = string;

/** Tracé entre les points de passage : direct (lissé) ou en traversées vers une difficulté visée. */
export type PisteRoute = 'direct' | Difficulty;

/** Gabarit d'une piste : la largeur dépend de la difficulté, multipliée par ce facteur. */
export type PisteSize = 'narrow' | 'normal' | 'wide';

export interface Piste {
  id: EntityId;
  rev: number;
  name: string;
  difficulty: Difficulty;
  path: Vec2[];
  width: number;
  length: number;
  drop: number;
  from: NodeKey;
  to: NodeKey;
  /** Hauteur de neige (cm). */
  snowDepth: number;
  /** Qualité de la neige 0..1 (damage, fraîcheur). */
  quality: number;
  /** Prévue au plan de damage (damée chaque nuit) ; sinon laissée en bosses. */
  groom: boolean;
  /** Fraîcheur du damage 0..1 : 1 = velours tout juste damé, usé par les passages et la neige fraîche. */
  groomed: number;
  open: boolean;
  manualClosed: boolean;
  closedReason: string | null;
  snowmaking: boolean;
  cannons: number;
  /** Skieurs actuellement sur la piste. */
  traffic: number;
  /** Points de passage choisis par le joueur (départ, intermédiaires, arrivée) ; vide pour un tracé généré. */
  waypoints: Vec2[];
  route: PisteRoute;
  size: PisteSize;
  /** Avancement des travaux (création ou reprise du tracé) 0..1 ; 1 : piste terminée. */
  progress: number;
}

export type BuildingStatus = 'construction' | 'open' | 'closed';

export interface Building {
  id: EntityId;
  rev: number;
  kind: BuildingKind;
  x: number;
  z: number;
  /** Rotation autour de y (radians). */
  rot: number;
  status: BuildingStatus;
  progress: number;
  /** Occupation (restaurants) ou activité, 0..1. */
  load: number;
  /** Piste équipée (enneigeurs). */
  pisteId: EntityId | null;
  /** Personnes dans la file du comptoir (caisses, location). */
  queue: number;
  /** Attente estimée au comptoir (s de simulation). */
  waitSec: number;
  /** Comptoir : clients servis aujourd'hui (forfaits vendus ou paires louées). */
  served: number;
  /** Comptoir : clients perdus aujourd'hui (renoncement devant la file, parc épuisé). */
  lost: number;
  /** Restauration : repas servis aujourd'hui. */
  meals: number;
  /** Restauration : clients refoulés aujourd'hui (salle pleine, denrées épuisées). */
  mealsLost: number;
}

/** Ventes manquées du jour, par cause. */
export interface MissedToday {
  /** Visiteurs refusés : parking plein. */
  parking: number;
  /** Visiteurs repartis : file aux caisses trop longue ou caisses fermées. */
  tickets: number;
  /** Locations manquées : file trop longue, parc épuisé ou aucune location ouverte. */
  rentals: number;
  /** Repas manqués : salles pleines ou denrées épuisées. */
  meals: number;
}

export interface StationStats {
  onDomain: number;
  visitorsToday: number;
  refusedToday: number;
  /** Satisfaction moyenne des visiteurs présents (0..100). */
  satisfaction: number;
  /** Réputation 0..1 (moyenne glissante sur les journées). */
  reputation: number;
  openPistes: number;
  openKm: number;
  openLifts: number;
  queueTotal: number;
  parkingCapacity: number;
  /** Ventes manquées depuis l'ouverture. */
  missed: MissedToday;
}

export interface StationPublic {
  id: EntityId;
  name: string;
  ownerId: string | null;
  stats: StationStats;
}

/* ------------------------------------------------------------------ */
/* Skieurs (agents)                                                     */
/* ------------------------------------------------------------------ */

/** Types de mouvement (premier élément de `MotionWire`). */
export const MOTION = { walk: 0, queue: 1, ride: 2, ski: 3, inside: 4, idle: 5, line: 6 } as const;

/**
 * Mouvement d'un skieur, sous forme de tableau compact. Le client calcule la
 * position à tout instant à partir de ce seul enregistrement (prédiction) :
 * - walk   [0, t0, dur, ax, az, bx, bz]
 * - queue  [1, t0, liftId]
 * - ride   [2, liftId, s0, seat]          position = s(t) - s0 le long du câble
 * - ski    [3, pisteId, t0, dur, lane, phase, s0?, s1?]   portion [s0, s1] de la trajectoire (toute la piste par défaut)
 * - inside [4, buildingId, t0, dur]
 * - idle   [5, t0, x, z]
 * - line   [6, t0, buildingId]            file devant un comptoir (caisses, location)
 */
export type MotionWire =
  | [0, number, number, number, number, number, number]
  | [1, number, EntityId]
  | [2, EntityId, number, number]
  | [3, EntityId, number, number, number, number, number?, number?]
  | [4, EntityId, number, number]
  | [5, number, number, number]
  | [6, number, EntityId];

export interface AgentWire {
  i: EntityId;
  /** Apparence : bits 0-3 couleur de veste, 4-5 niveau, 6-7 variante. */
  c: number;
  m: MotionWire;
}

export function agentJacket(c: number): number {
  return c & 15;
}
export function agentSkill(c: number): number {
  return (c >> 4) & 3;
}
export function agentVariant(c: number): number {
  return (c >> 6) & 3;
}
export function packAppearance(jacket: number, skill: number, variant: number): number {
  return (jacket & 15) | ((skill & 3) << 4) | ((variant & 3) << 6);
}

/* ------------------------------------------------------------------ */
/* Joueur (privé)                                                       */
/* ------------------------------------------------------------------ */

export interface Stock {
  qty: number;
  capacity: number;
  /** Consommation sur la dernière journée (unités/jour), pour estimer l'autonomie. */
  dailyUse: number;
}

export interface SupplyOrder {
  id: EntityId;
  supply: SupplyKind;
  qty: number;
  unitPrice: number;
  supplier: string;
  /** Livraison prévue (horloge de jeu). */
  eta: number;
}

export interface FinanceDay {
  day: number;
  passes: number;
  food: number;
  rental: number;
  /** Nuitées des hébergements (hôtels, résidences), encaissées en début de journée. */
  lodging: number;
  /** Cours de l'école de ski. */
  lessons: number;
  staff: number;
  energy: number;
  upkeep: number;
  supplies: number;
  construction: number;
  repairs: number;
  visitors: number;
  refused: number;
  satisfaction: number;
}

/** Recettes d'une journée (forfaits, restauration, location, hébergement, cours). */
export function dayRevenue(d: FinanceDay): number {
  return d.passes + d.food + d.rental + (d.lodging ?? 0) + (d.lessons ?? 0);
}

/** Charges d'une journée. */
export function dayCosts(d: FinanceDay): number {
  return d.staff + d.energy + d.upkeep + d.supplies + d.construction + d.repairs;
}

export type AlertLevel = 'info' | 'ok' | 'warn' | 'danger';

export interface AlertAction {
  label: string;
  command: import('./commands').Command;
}

export interface Alert {
  id: EntityId;
  /** Clé de déduplication (ex. "lift-broken:12"). */
  key: string;
  level: AlertLevel;
  title: string;
  message: string;
  gameTime: number;
  action: AlertAction | null;
  /** Cible pour centrer la caméra. */
  focus: { kind: 'lift' | 'piste' | 'building'; id: EntityId } | null;
}

export interface PlayerPrivate {
  id: string;
  stationId: EntityId;
  cash: number;
  ticketPrice: number;
  maintenance: 0 | 1 | 2;
  stocks: { water: Stock; fuel: Stock; food: Stock };
  orders: SupplyOrder[];
  today: FinanceDay;
  history: FinanceDay[];
  alerts: Alert[];
  /** Damage de la nuit en cours ou de la dernière nuit. */
  grooming: import('./grooming').GroomingNight | null;
}

/* ------------------------------------------------------------------ */
/* Événements ponctuels (toasts, sons)                                  */
/* ------------------------------------------------------------------ */

export type GameEvent =
  | { k: 'built'; id: EntityId; label: string }
  | { k: 'breakdown'; id: EntityId; label: string }
  | { k: 'repaired'; id: EntityId; label: string }
  | { k: 'delivery'; supply: SupplyKind; qty: number }
  | { k: 'dayEnd'; report: FinanceDay }
  | { k: 'notice'; level: AlertLevel; title: string; message: string };
